import { useState, useEffect } from 'react';
import {
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  Divider,
  Stack,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { useAuthStore } from '@/store/auth.store';
import { useNavigate } from 'react-router-dom';
import {
  initiateGoogleLogin,
  isGoogleAuthAvailable,
} from '@/services/googleAuthService';

export const LoginPage = () => {
  const brandLogoSrc = '/asneris.png';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setError('');
      await initiateGoogleLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate Google Sign-In');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password);
      const hasAgreed = localStorage.getItem('data_disclaimer_agreed') === 'true';
      navigate(hasAgreed ? '/' : '/disclaimer');
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'radial-gradient(circle at top right, rgba(78, 184, 197, 0.10), transparent 35%), #f6f8fc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2.5, md: 3 },
      }}
    >
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: 1220,
          mx: 'auto',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(7, 32, 76, 0.10)',
        }}
      >
        <Grid
          item
          md={5}
          lg={5}
          sx={{
            display: { xs: 'none', md: 'block' },
            background: 'linear-gradient(145deg, #06295f 0%, #164e91 55%, #1c73ba 100%)',
          }}
        >
          <Box sx={{ height: '100%', p: { md: 3, lg: 3.5 }, color: '#e8f2ff', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.8 }}>
              <Box
                component="img"
                src={brandLogoSrc}
                alt="Asneris"
                sx={{ width: 30, height: 30, objectFit: 'contain' }}
              />
              <Stack spacing={0.1}>
                <Typography sx={{ color: '#ffffff', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                <Typography variant="caption" sx={{ color: '#b9d8fb', letterSpacing: '0.1em', fontWeight: 700 }}>
                  SEO / GEO Engine
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ fontSize: { md: 28, lg: 32 }, fontWeight: 800, lineHeight: 1.2, mb: 1.5, color: '#ffffff' }}>
              Improve Your Search & AI Discoverability
            </Typography>

            <Typography variant="body1" sx={{ color: '#d8eaff', lineHeight: 1.6, mb: 2.3 }}>
              Manage technical SEO, indexing, analytics and AI-search readiness from one workspace.
            </Typography>

            <Stack spacing={1.05} sx={{ mb: 2.4 }}>
              {[
                'Technical SEO monitoring',
                'Search Console integration',
                'Analytics and reporting',
                'Automation and alerts',
              ].map((item) => (
                <Stack key={item} direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: '#98d7ff', fontSize: 18 }} />
                  <Typography sx={{ color: '#d9ecff', fontWeight: 600 }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>

            <Box
              sx={{
                mt: 1,
                borderRadius: 2,
                p: 2,
                background: 'linear-gradient(140deg, rgba(128, 188, 255, 0.18), rgba(128, 188, 255, 0.05))',
                border: '1px solid rgba(170, 216, 255, 0.22)',
              }}
            >
              <Stack spacing={0.7} alignItems="center">
                <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>Website</Typography>
                <Typography variant="caption" sx={{ color: '#acd4ff' }}>↓</Typography>
                <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>SEO signals</Typography>
                <Typography variant="caption" sx={{ color: '#acd4ff' }}>↓</Typography>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <TrendingUpRoundedIcon sx={{ color: '#9cd6ff', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>
                    Search and AI visibility
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Typography variant="body2" sx={{ mt: 'auto', pt: 2.2, color: '#d7e9ff' }}>
              Secure, reliable and built for modern teams.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={7} lg={7}>
          <Box
            sx={{
              minHeight: { xs: 'auto', md: 760 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 0, sm: 1.5, md: 3.4, lg: 4.2 },
              py: { xs: 0, sm: 1.5, md: 2 },
              backgroundColor: '#f6f8fc',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 452 }}>
              <Stack direction="row" spacing={1.1} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mb: 1.6 }}>
                <Box
                  component="img"
                  src={brandLogoSrc}
                  alt="Asneris"
                  sx={{ width: 26, height: 26, objectFit: 'contain' }}
                />
                <Stack spacing={0.1}>
                  <Typography sx={{ color: '#173f79', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                  <Typography variant="caption" sx={{ color: '#5f7ea8', fontWeight: 700 }}>SEO / GEO Engine</Typography>
                </Stack>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  p: { xs: 2.6, sm: 3.2, md: 4 },
                  borderRadius: 2,
                  border: '1px solid #dbe4ef',
                  boxShadow: '0 20px 50px rgba(6, 41, 95, 0.10), 0 4px 12px rgba(15, 23, 42, 0.05)',
                  backgroundColor: '#ffffff',
                }}
              >
              <Typography
                component="h1"
                variant="h4"
                sx={{ fontWeight: 800, color: '#142f57', letterSpacing: '-0.02em', fontSize: { xs: '1.9rem', sm: '2rem' }, textAlign: 'center' }}
                gutterBottom
              >
                Welcome back
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2.5, sm: 3.2 }, textAlign: 'center' }}>
                Sign in to continue to your Asneris workspace.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Box component="form" onSubmit={handleLogin}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  disabled={!isGoogleAuthAvailable()}
                  onClick={handleGoogleLogin}
                  sx={{
                    height: { xs: 48, sm: 50 },
                    borderRadius: 1.3,
                    borderColor: '#cfd8e6',
                    color: isGoogleAuthAvailable() ? 'text.primary' : 'text.disabled',
                    backgroundColor: '#ffffff',
                    fontWeight: 700,
                    '&:hover': {
                      borderColor: '#bcc9dd',
                      backgroundColor: '#fdfefe',
                    },
                    mb: 3,
                  }}
                >
                  {isGoogleAuthAvailable() ? 'Continue with Google' : 'Google Sign-In not configured'}
                </Button>

                <Divider sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    or continue with email
                  </Typography>
                </Divider>

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 1.4,
                    height: 52,
                    borderRadius: 1.3,
                    background: 'linear-gradient(90deg, #164e91 0%, #1c73ba 100%)',
                    boxShadow: '0 10px 24px rgba(22, 78, 145, 0.24)',
                    fontWeight: 800,
                    '&:hover': {
                      background: 'linear-gradient(90deg, #124583 0%, #1969ac 100%)',
                      boxShadow: '0 14px 28px rgba(22, 78, 145, 0.34)',
                    },
                  }}
                  size="large"
                  disabled={isSubmitting}
                  endIcon={!isSubmitting ? <ArrowForwardRoundedIcon /> : undefined}
                >
                  {isSubmitting ? 'Signing in…' : 'Sign in'}
                </Button>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 0.6, sm: 0 },
                    mb: 1.5,
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox size="small" defaultChecked />}
                    label={<Typography variant="caption" color="text.secondary">Remember me</Typography>}
                  />
                  <Link component="button" variant="body2" onClick={() => navigate('/forgot-password')}>
                    Forgot password?
                  </Link>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 0.6 }}>
                  Don&apos;t have an account?{' '}
                  <Link component="button" variant="body2" sx={{ fontWeight: 700 }} onClick={() => navigate('/register')}>
                    Create an account
                  </Link>
                </Typography>
              </Box>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Need help? Contact support@asneris.com
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
